import { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TablePagination from "@/components/ui/table-pagination.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cardStyles } from "@/lib/status-styles";
import { formatStartTime } from "@/lib/datetime-utils.js";
import { formatNumber } from "@/lib/formatting-utils.js";
import { getOngoingDowntimeBadgeClass } from "@/lib/downtime-duration-styles.js";
import { mathUtils } from "@/lib/utils.js";
import DowntimeTypeBadge from "./downtime-type-badge.jsx";
import SortableTableHeader from "./ui/sortable-table-header.jsx";

/**
 * Downtime Events Table Component
 * Displays paginated downtime events with filtering by selected month
 * @param {Object} props
 * @param {Array} props.events - Array of downtime events to display
 * @param {string|null} props.selectedMonth - Currently selected month for filtering
 * @param {boolean} props.isLoading - Loading state
 * @param {string|null} props.error - Error message
 * @param {boolean} [props.isEnbaseAndEnalysis] - When true, show blue bar on first column (e.g. when used for a single asset that has optimization enabled)
 * @returns {JSX.Element} Downtime Events Table component
 */
export default function DowntimeEventsTable({
  events,
  selectedMonth,
  isLoading,
  error,
  isEnbaseAndEnalysis = false,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  /** @type {[number, Function]} */
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Reset to page 1 when events change (e.g., when filter changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, events.length]);

  // Reset to page 1 when rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  /**
   * Handles column header click for sorting
   * @param {string} column - Column name to sort by
   */
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  /**
   * Sort downtime events by column
   * @param {Array} events - Array of events to sort
   * @param {string} column - Column to sort by
   * @param {string} direction - Sort direction ('asc' or 'desc')
   * @returns {Array} Sorted events array
   */
  const sortDowntimeEventsByColumn = (events, column, direction) => {
    if (!Array.isArray(events)) return [];

    return [...events].sort((a, b) => {
      let aValue, bValue;

      switch (column) {
        case "ReportingDate":
          aValue = new Date(a.ReportingDate || 0);
          bValue = new Date(b.ReportingDate || 0);
          if (aValue < bValue) return direction === "asc" ? -1 : 1;
          if (aValue > bValue) return direction === "asc" ? 1 : -1;
          return 0;

        case "DowntimeHours":
          // Calculate hours for comparison (handles ongoing events)
          const getHours = (event) => {
            if (
              event.DowntimeHours !== null
              && event.DowntimeHours !== undefined
            ) {
              return event.DowntimeHours;
            }
            // For ongoing events, calculate from ReportingDate to now
            if (event.ReportingDate && !event.ClosingDate) {
              const startDate = new Date(event.ReportingDate);
              const now = new Date();
              return (now.getTime() - startDate.getTime()) / (1000 * 60 * 60);
            }
            return 0;
          };
          aValue = getHours(a);
          bValue = getHours(b);
          if (aValue < bValue) return direction === "asc" ? -1 : 1;
          if (aValue > bValue) return direction === "asc" ? 1 : -1;
          return 0;

        case "RelatedAlarm":
          aValue = String(a.RelatedAlarm || "");
          bValue = String(b.RelatedAlarm || "");
          const comparison = aValue.localeCompare(bValue);
          return direction === "asc" ? comparison : -comparison;

        case "Status":
          aValue = String(a.Status || "");
          bValue = String(b.Status || "");
          const statusComparison = aValue.localeCompare(bValue);
          return direction === "asc" ? statusComparison : -statusComparison;

        case "IsMechanical":
          aValue = a.IsMechanical ?? 0;
          bValue = b.IsMechanical ?? 0;
          if (aValue < bValue) return direction === "asc" ? -1 : 1;
          if (aValue > bValue) return direction === "asc" ? 1 : -1;
          return 0;

        default:
          return 0;
      }
    });
  };

  // Apply sorting to events
  const sortedEvents = useMemo(() => {
    if (!sortColumn) return events;
    return sortDowntimeEventsByColumn(events, sortColumn, sortDirection);
  }, [events, sortColumn, sortDirection]);

  const totalEvents = sortedEvents.length;
  const totalPages = mathUtils.ceil(totalEvents / itemsPerPage);

  // Reset to page 1 if current page exceeds total pages (e.g., after filtering or sorting)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const adjustedCurrentPage =
    currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
  const startIndex = (adjustedCurrentPage - 1) * itemsPerPage;
  const endIndex = mathUtils.min(startIndex + itemsPerPage, totalEvents);
  const paginatedEvents = sortedEvents.slice(startIndex, endIndex);

  // Helper function to check if event is ongoing and calculate hours
  const getDowntimeHours = (event) => {
    // Check if event is ongoing (no ClosingDate or DowntimeHours is null/undefined)
    const isOngoing =
      !event.ClosingDate
      && (event.DowntimeHours === null || event.DowntimeHours === undefined);

    if (isOngoing && event.ReportingDate) {
      // Calculate hours from ReportingDate to now
      const startDate = new Date(event.ReportingDate);
      const now = new Date();
      const hoursDiff =
        (now.getTime() - startDate.getTime()) / (1000 * 60 * 60);
      return { hours: hoursDiff, isOngoing: true };
    }

    // Return closed event hours
    return {
      hours:
        event.DowntimeHours !== null && event.DowntimeHours !== undefined
          ? event.DowntimeHours
          : null,
      isOngoing: false,
    };
  };

  return (
    <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden'>
      <CardHeader className='py-3 px-4 border-b border-border'>
        <div className='flex items-center gap-2'>
          <AlertTriangle className='h-5 w-5 text-muted-foreground' />
          <CardTitle className='text-base font-medium text-foreground'>
            Downtime Events
            {totalEvents > 0 && (
              <span className='font-normal text-muted-foreground ml-2'>
                ({totalEvents} total)
              </span>
            )}
          </CardTitle>
          {selectedMonth && (
            <Badge
              variant='outline'
              className='bg-primary/10 text-primary border-primary/30'>
              {selectedMonth}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className={cardStyles.content}>
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
            <span className='ml-3 text-muted-foreground'>
              Loading downtime events...
            </span>
          </div>
        ) : error ? (
          <div className='flex items-center justify-center py-8'>
            <div className='text-center'>
              <AlertTriangle className='h-8 w-8 text-red-500 mx-auto mb-2' />
              <p className='text-red-600 mb-2'>{error}</p>
              <p className='text-sm text-muted-foreground'>
                Please try refreshing the page
              </p>
            </div>
          </div>
        ) : !events || events.length === 0 ? (
          <div className='flex items-center justify-center py-8'>
            <div className='text-center'>
              {selectedMonth && (
                <p className='text-muted-foreground mb-2'>
                  No events in selected month
                </p>
              )}
              {!selectedMonth && (
                <p className='text-muted-foreground mb-2'>
                  No downtime events found
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={cardStyles.tableContainer}>
              <Table className='w-full'>
                <TableHeader className={cardStyles.tableHeader}>
                  <TableRow className={cardStyles.tableRow}>
                    <TableHead
                      className={`${cardStyles.tableHead} py-1.5 font-normal`}>
                      <SortableTableHeader
                        column='ReportingDate'
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onClick={() => handleSort("ReportingDate")}>
                        Date/Time
                      </SortableTableHeader>
                    </TableHead>
                    <TableHead
                      className={`${cardStyles.tableHead} text-right py-1.5 font-normal`}>
                      <SortableTableHeader
                        column='DowntimeHours'
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onClick={() => handleSort("DowntimeHours")}
                        className='justify-end'>
                        Downtime
                      </SortableTableHeader>
                    </TableHead>
                    <TableHead
                      className={`${cardStyles.tableHead} py-1.5 font-normal`}>
                      <SortableTableHeader
                        column='Status'
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onClick={() => handleSort("Status")}>
                        Status
                      </SortableTableHeader>
                    </TableHead>
                    <TableHead
                      className={`${cardStyles.tableHead} py-1.5 font-normal`}>
                      <SortableTableHeader
                        column='RelatedAlarm'
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onClick={() => handleSort("RelatedAlarm")}>
                        Related Alarm
                      </SortableTableHeader>
                    </TableHead>
                    <TableHead
                      className={`${cardStyles.tableHead} py-1.5 font-normal`}>
                      <SortableTableHeader
                        column='IsMechanical'
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onClick={() => handleSort("IsMechanical")}>
                        Mechanical?
                      </SortableTableHeader>
                    </TableHead>
                    <TableHead className='hidden'>UniqueId</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEvents.map((event, index) => (
                    <TableRow
                      key={event.UniqueId || startIndex + index}
                      className={cardStyles.bodyRow}>
                      <TableCell
                        className={`text-sm text-foreground font-normal py-3 px-4 relative ${
                          isEnbaseAndEnalysis ? "pl-5" : ""
                        }`}>
                        {isEnbaseAndEnalysis && (
                          <div
                            className='absolute left-0 top-0 bottom-0 w-[3px] bg-brand-cyan z-0'
                            aria-hidden
                          />
                        )}
                        <span className='relative z-0'>
                          {event.ReportingDate ? (
                            <span className='text-sm font-mono font-normal'>
                              {formatStartTime(event.ReportingDate)}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </span>
                      </TableCell>
                      <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
                        {(() => {
                          const { hours, isOngoing } = getDowntimeHours(event);
                          if (hours === null) {
                            return "N/A";
                          }
                          return (
                            <div className='flex items-center justify-end gap-2'>
                              {isOngoing && (
                                <Badge
                                  variant='status'
                                  className={getOngoingDowntimeBadgeClass()}>
                                  Ongoing
                                </Badge>
                              )}
                              <span className='text-sm font-mono font-normal'>
                                {formatNumber(hours, 2)} h
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                        {event.Status}
                      </TableCell>
                      <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                        {event.RelatedAlarm || "—"}
                      </TableCell>
                      <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
                        <DowntimeTypeBadge type={event.IsMechanical} />
                      </TableCell>
                      <TableCell className='hidden'>
                        {event.UniqueId || ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            <TablePagination
              currentPage={adjustedCurrentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalEvents}
              startIndex={startIndex}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              itemsPerPageOptions={[10, 20, 50, 100]}
              itemsLabel='events'
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
